import { useState, useEffect, useRef } from "react";
import * as React from 'react';
import dayjs from 'dayjs';
import CloseIcon from "@mui/icons-material/Close";
import { createPortal } from "react-dom";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { useMemo } from 'react';
import CustomButton from "./Button/CustomButton";
import { useTranslation } from "react-i18next";

const LOCALE_MAP = {
  en: "en",
  "en-GB": "en-gb",
  fr: "fr",
  de: "de",
  es: "es",
  it: "it",
  nl: "nl",
  pl: "pl",
  pt: "pt",
  sv: "sv",
  ar: "ar",
  tr: "tr",
};

export function usePopover(
  anchorRef,
  isOpen,
  onClose,
  options = {},
  popRef = null
) {
  const {
    popWidth = 300,
    gap = 8,
    edge = 16,
    offsetY = -100,
  } = options;

  const [pos, setPos] = useState({ top: 0, left: 0, placement: "bottom" });

  useEffect(() => {
    if (!isOpen) return;

    const place = () => {
      const anchorEl = anchorRef.current;
      if (!anchorEl) return;

      const r = anchorEl.getBoundingClientRect();

      // Measure actual popover height if possible; fall back to a sensible default
      const popEl = popRef?.current || null;
      const measuredH = popEl?.offsetHeight || 260;

      // Use visualViewport on mobile for accurate available height
      const viewportW = window.innerWidth;
      const viewportH = window.visualViewport?.height || window.innerHeight;

      // Horizontal
      const popW = Math.min(popWidth, Math.floor(viewportW - edge * 2));
      let left = r.left;
      if (left + popW > viewportW - edge) left = r.right - popW;
      left = Math.max(edge, Math.min(left, viewportW - popW - edge));

      // Vertical (prefer bottom)
      let placement = "bottom";
      let top = r.bottom + gap + offsetY;

      if (top + measuredH > viewportH - edge) {
        // Not enough room below → try opening above
        placement = "top";
        top = r.top - gap - measuredH - offsetY;

        // If still not enough room above, clamp within viewport and revert to bottom
        if (top < edge) {
          placement = "bottom";
          // Clamp so the popover stays within safe area
          const maxBottomTop = Math.max(edge, Math.min(r.bottom + gap + offsetY, viewportH - edge - measuredH));
          top = maxBottomTop;
        }
      }

      setPos({ top, left, placement });
    };

    // Initial + reactive placement
    place();

    const onSR = () => place();
    window.addEventListener("resize", onSR);
    window.addEventListener("scroll", onSR, true);

    // Close on outside click / ESC
    const onDoc = (e) => {
      const a = anchorRef.current;
      const inAnchor = a && a.contains(e.target);
      const inPop = (e.target instanceof Element) && e.target.closest(".pop");
      if (!inAnchor && !inPop) onClose?.();
    };
    const onEsc = (e) => e.key === "Escape" && onClose?.();

    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);

    return () => {
      window.removeEventListener("resize", onSR);
      window.removeEventListener("scroll", onSR, true);
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [anchorRef, isOpen, onClose, popWidth, gap, edge, offsetY, popRef]);

  return pos;
}

/* ---------- Time popover (no new deps; respects minStep) ---------- */
function TimePopover({
  anchorRef,
  open,
  onClose,
  value,           // Dayjs | undefined
  onPick,          // (d: Dayjs) => void
  step = 15,       // minutes step
  title = "Pick a time"
}) {
  const { t } = useTranslation("global");
  const pos = usePopover(anchorRef, open, onClose);
  const initial = useMemo(() => (value ? dayjs(value) : dayjs().hour(9).minute(0).second(0).millisecond(0)), [value]);
  const [h, setH] = useState(initial.hour());
  const [m, setM] = useState(Math.floor(initial.minute() / step) * step);

  useEffect(() => {
    if (!open) return;
    const v = value ? dayjs(value) : dayjs();
    setH(v.hour());
    setM(Math.floor(v.minute() / step) * step);
  }, [open, value, step]);

  if (!open) return null;

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = Array.from({ length: Math.floor(60 / step) }, (_, i) => i * step);

  const commit = () => {
    const next = (value ? dayjs(value) : dayjs()).hour(h).minute(m).second(0).millisecond(0);
    onPick(next);   // <--- sends Dayjs back
    onClose();
  };
    const viewportW = window.innerWidth;

//<div className="dtp2" style={{ top: viewportW<721 ? "5px":pos.top, left: pos.left }}></div>
//<div className={`pop pop--${pos.placement}`} style={{ top: "25vh", left: pos.left }}>
  return createPortal(
    <div className={`pop pop--${pos.placement}`} style={{ top: viewportW<721 ? "25vh":pos.top, left: pos.left }}>
      <div className="pop__head">
        <span>{title}</span>
        <button type="button" className="icon-btn" onClick={onClose}>✕</button>
      </div>

      <div className="pop__body">
        <div className="time-grid">
          <div className="time-col">
            <div className="time-col__label">{t('picker.time.hour')}</div>
            <div className="time-list">
              {hours.map((hh) => (
                <button
                  key={hh}
                  type="button"
                  className={`time-chip ${hh === h ? "is-active" : ""}`}
                  onClick={() => setH(hh)}
                >
                  {String(hh).padStart(2, "0")}
                </button>
              ))}
            </div>
          </div>

          <div className="time-col">
            <div className="time-col__label">{t('picker.time.minute')}</div>
            <div className="time-list">
              {minutes.map((mm) => (
                <button
                  key={mm}
                  type="button"
                  className={`time-chip ${mm === m ? "is-active" : ""}`}
                  onClick={() => setM(mm)}
                >
                  {String(mm).padStart(2, "0")}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="time-actions">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          {/* <button type="button" className="btn-primary" onClick={commit}>
            Select {String(h).padStart(2, "0")}:{String(m).padStart(2, "0")}
          </button> */}
          <CustomButton className="app-btn--sm" onClick={commit} label={'Select ' + String(h).padStart(2, "0")+':'+String(m).padStart(2, "0")} loading={false} />
        </div>
      </div>
    </div>,
    document.body
  );
}

function ensureDayjsLocale(locale) {
  try {
    // dynamic import for the specific locale (bundlers will code-split this)
    // remove the await if your env doesn’t support top-level await in modules
    // eslint-disable-next-line no-undef
    return import(`dayjs/locale/${locale}.js`);
  } catch {
    return Promise.resolve(); // fall back silently
  }
}

const normalizeToDayjsLocale = (lang) => {
  if (!lang) return "en";
  const lower = lang.toLowerCase();
  return LOCALE_MAP[lower] || LOCALE_MAP[lang] || lower || "en";
};

export function DatePopover({
  shouldDisablePast,
  anchorRef,
  open,
  onClose,
  value,
  onPick,
  title,
  shiftUp,
}) {
  const { i18n, t } = useTranslation();

  // resolve dayjs locale from current i18n language
  const dayjsLocale = useMemo(() => {
    const lang = i18n.language || "en";
    // normalize e.g. en-US → en-us to match dayjs naming
    const normalized = lang.toLowerCase();
    return LOCALE_MAP[normalized] || LOCALE_MAP[lang] || "en";
  }, [i18n.language]);

  useEffect(() => {
    // load and set the locale whenever language changes
    ensureDayjsLocale(dayjsLocale).finally(() => {
      dayjs.locale(dayjsLocale);
    });
  }, [dayjsLocale]);

  // --- your existing popover positioning ---
  const pos = usePopover(anchorRef, open, onClose);
  if (!open) return null;

  const djValue = value ? dayjs(value) : null;
  const viewportW = window.innerWidth;

  return createPortal(
    <div
      className={`pop pop--${pos.placement}`}
      style={{
        top: viewportW < 721 ? "25vh" : shiftUp === true ? pos.top - 150 : pos.top,
        left: pos.left,
      }}
    >
      <div className="pop__head">
        <span>{title ?? t("common.selectDate", "Select Date")}</span>
        <button type="button" className="icon-btn" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </button>
      </div>

      <div className="pop__body">
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale={dayjsLocale}
        >
          <StaticDatePicker
            value={djValue}
            minDate={shouldDisablePast ? dayjs().startOf("day"): null}
            disablePast={shouldDisablePast}
            onChange={(d) => {
              if (!d || !d.isValid()) return;
              onPick(d.toDate()); // return native Date consistently
              onClose();
            }}
            displayStaticWrapperAs="desktop"
            slotProps={{ toolbar: { hidden: true }, actionBar: { actions: [] } }}
            format="DD/MM/YYYY"
          />
        </LocalizationProvider>
      </div>
    </div>,
    document.body
  );
}



export default function DateTimePopoverPro({
  anchorRef,
  open,
  onClose,
  value,
  minuteStep = 15,
  disablePast = false,
  isDateAllowed,
  onApply,
}) {
  const { t } = useTranslation("global");
  const pos = useSmartPlacement2(anchorRef, open);
  const now = new Date();
  const { i18n } = useTranslation();
  const dayjsLocale = useMemo(
    () => normalizeToDayjsLocale(i18n.language),
    [i18n.language]
  );

  // 👇 Use YOUR ensureDayjsLocale
  useEffect(() => {
    ensureDayjsLocale(dayjsLocale).finally(() => {
      dayjs.locale(dayjsLocale);
    });
  }, [dayjsLocale]);

  const initial = useMemo(() => {
    const base = value ? new Date(value) : new Date();
    return clampToMinute(base, minuteStep);
  }, [value, minuteStep]);

  const [selDate, setSelDate] = useState(initial);
  const [selTime, setSelTime] = useState(
    `${initial.getHours().toString().padStart(2, "0")}:${initial
      .getMinutes()
      .toString()
      .padStart(2, "0")}`
  );

  useEffect(() => {
    if (!open) return;
    const onEsc = (e) => e.key === "Escape" && onClose?.();
    const onDoc = (e) => {
      const inAnchor = anchorRef.current && anchorRef.current.contains(e.target);
      const inPop = e.target.closest?.(".dtp2");
      if (!inAnchor && !inPop) onClose?.();
    };
    document.addEventListener("keydown", onEsc);
    document.addEventListener("mousedown", onDoc);
    return () => {
      document.removeEventListener("keydown", onEsc);
      document.removeEventListener("mousedown", onDoc);
    };
  }, [open, onClose, anchorRef]);

  useEffect(() => {
    if (!open) return;
    setSelDate(initial);
    setSelTime(
      `${initial.getHours().toString().padStart(2, "0")}:${initial
        .getMinutes()
        .toString()
        .padStart(2, "0")}`
    );
  }, [open, initial]);

  const times = useMemo(() => buildTimes(minuteStep), [minuteStep]);

  const isTimeDisabled = (timeStr) => {
    if (!disablePast) return false;
    const [hh, mm] = timeStr.split(":").map(Number);
    const d = new Date(selDate);
    d.setHours(hh, mm, 0, 0);
    return d.getTime() < now.getTime() - 1000;
  };

  const apply = () => {
    const [hh, mm] = selTime.split(":").map(Number);
    const out = new Date(selDate);
    out.setHours(hh, mm, 0, 0);
    onApply?.(out);
    onClose?.();
  };

  const viewportW = window.innerWidth;
  if (!open) return null;

  return createPortal(
    <div className="dtp2" style={{ top: viewportW < 721 ? "5px" : pos.top, left: pos.left }}>
      <div className="dtp2__head">
        <span>{t('picker.date.selectDate')} &amp; {t('picker.time.time_lowercase')}</span>
        <button type="button" className="dtp2__close" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </button>
      </div>

      <div className="dtp2__body">
        <div className="dtp2__grid">
          <div className="dtp2__cal">
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale={dayjsLocale}  // ← month/weekday header localization
            >
              <StaticDatePicker
                displayStaticWrapperAs="desktop"
                value={dayjs(selDate)}
                onChange={(d) => {
                  if (!d) return;
                  const picked = d.toDate();
                  const [hh, mm] = selTime.split(":").map(Number);
                  picked.setHours(hh, mm, 0, 0);
                  setSelDate(picked);
                }}
                shouldDisableDate={(d) => {
                  const js = d?.toDate();
                  if (!js) return false;
                  if (disablePast) {
                    const startOfDay = new Date();
                    startOfDay.setHours(0, 0, 0, 0);
                    if (js < startOfDay) return true;
                  }
                  if (typeof isDateAllowed === "function") {
                    return !isDateAllowed(js);
                  }
                  return false;
                }}
                slotProps={{ actionBar: { actions: [] } }}
              />
            </LocalizationProvider>
          </div>

          <div className="dtp2__time">
            <div className="dtp2__timeTitle">{t('picker.time.pickATime')}</div>
            <div className="dtp2__timeList">
              {times.map((t) => {
                const disabled = isTimeDisabled(t);
                const isActive = t === selTime;
                return (
                  <button
                    key={t}
                    type="button"
                    className={`dtp2-timeBtn${isActive ? " is-active" : ""}${disabled ? " is-disabled" : ""}`}
                    disabled={disabled}
                    onClick={() => setSelTime(t)}
                    aria-pressed={isActive}
                    title={disabled ? "In the past" : ""}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="time-actions p-2">
        <button type="button" className="btn-ghost" onClick={onClose}>{t('cancel')}</button>
        <CustomButton
          className="app-btn--sm"
          onClick={apply}
          disabled={!selTime}
          label={selTime ? `${t('select')} ${selTime}` : t('select')}
          loading={false}
        />
      </div>
    </div>,
    document.body
  );
}

 /* ---------- Inline inputs that open the popovers ---------- */
export function InlineDateInput({ shouldDisablePast = true, label, subLabel, value, onChange, error, required, shiftUp = false }) {
  const { t } = useTranslation("global");
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  return (
    <div className={`sm-field ${error ? "is-error" : ""}`}>
      <label className="sm-label" style={{marginBottom:0}}>
        {label} {required && <span className="sm-req">*</span>}
      </label>
      <span className="sm-hint">{subLabel}</span>
      <button
        ref={ref}
        type="button"
        className="sm-input sm-input--picker"
        style={{minHeight:0}}
        onClick={() => setOpen(true)}
      >
        {value ? dayjs(value).format("DD/MM/YYYY") : t('picker.date.selectDate')}
      </button>
      {error && <div className="sm-error">{error}</div>}
      <DatePopover
        shouldDisablePast = {shouldDisablePast}
        anchorRef={ref}
        open={open}
        onClose={() => setOpen(false)}
        value={value}
        onPick={onChange}
        title={t('picker.date.selectDate')}
        shiftUp = {shiftUp}
      />
    </div>
  );
}

export function InlineTimeInput({ label, value, onChange, step = 15, error }) {
  const { t } = useTranslation("global");
  const ref = React.useRef(null);
  const [open, setOpen] = React.useState(false);
  const text = value ? dayjs(value).format("HH:mm") : "Select";

  return (
    <div className="inline-field">
      <label className="sm-label">{label}</label>
      <button
        type="button"
        ref={ref}
        className="sm-input sm-input--inline"
        onClick={() => setOpen(true)}
      >
        {text}
      </button>
        {error && <div className="sm-error">{error}</div>}
      <TimePopover
        anchorRef={ref}
        open={open}
        onClose={() => setOpen(false)}
        value={value}
        step={step}
        title={t('picker.time.pickATime')}
        onPick={(d) => { onChange(d); setOpen(false); }}
      />
    </div>
  );
}

export function InlineDateTimeInputPro({
  label,
  required,
  value,
  onChange,           // (Date)=>void
  minuteStep = 15,
  disablePast = true,
  isDateAllowed,      // (Date)=>boolean
  disabled
}) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);

  return (
    <div className="sm-field">
      <label className="sm-label">
        {label} {required && <span className="sm-req">*</span>}
      </label>
      <button
        ref={ref}
        type="button"
        className={`sm-input--picker ${disabled === true ? "sm-input-disabled" : "sm-input"}`}
        onClick={() => setOpen(true)}
      >
        {value
          ? dayjs(value).format("DD/MM/YYYY - HH:mm")
          : "Select…"}
      </button>

      <DateTimePopoverPro
        anchorRef={ref}
        open={open && !disabled}
        onClose={() => setOpen(false)}
        value={value}
        minuteStep={minuteStep}
        disablePast={disablePast}
        isDateAllowed={isDateAllowed}
        onApply={(d) => onChange?.(d)}
      />
    </div>
  );
}






function useSmartPlacement2(anchorRef, isOpen) {
  const [pos, setPos] = useState({ top: 0, left: 0 });
  useEffect(() => {
    if (!isOpen) return;
    const calc = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const popW = Math.min(920, Math.floor(window.innerWidth * 0.96));
      const popH = 480;
      const gap = 8;
      const yOffset = window.innerWidth <= 768 ? 0 : 12;
      const edge = 12;
      let left = r.left;
      if (left + popW > window.innerWidth - edge) left = r.right - popW;
      left = Math.max(edge, Math.min(left, window.innerWidth - popW - edge));
      let top = r.bottom + gap + yOffset;
      if (top + popH > window.innerHeight - edge) {
        // open upwards if not enough space
        top = Math.max(edge, r.top - gap - popH - yOffset);
      }
      setPos({ top, left });
    };
    calc();
    const onScrollOrResize = () => calc();
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    return () => {
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [anchorRef, isOpen]);
  return pos;
}

/* ---------- utils ---------- */
function clampToMinute(value, minute) {
  const d = new Date(value);
  d.setSeconds(0, 0);
  const m = d.getMinutes();
  d.setMinutes(m - (m % minute));
  return d;
}

function buildTimes(step) {
  const out = [];
  for (let m = 0; m < 24 * 60; m += step) {
    const hh = Math.floor(m / 60);
    const mm = m % 60;
    out.push(`${hh.toString().padStart(2, "0")}:${mm.toString().padStart(2, "0")}`);
  }
  return out;
}
